// @ts-strict-ignore
import { UpdateAppSettingsSuccessAction } from '@/actions/appSettings';
import {
    RELEASES_UPDATED,
    ReleasesUpdatedAction,
    UPDATE_RELEASE_SUCCESS,
    UpdateReleaseSuccessAction,
} from '@/actions/release';
import {
    FETCH_RELEASE_STEP_SUCCESS,
    FetchReleaseStepSuccessAction,
    TryUpdateReleaseStepsAction,
    UPDATE_RELEASE_STEPS,
} from '@/actions/releaseStep';
import {
    FeatureItem,
    PullRequest,
    Release,
    ReleaseStep,
    ReleaseStepType,
    TranslationProviderState,
    TranslationString,
} from '@/types';
import { combineReducers } from 'redux';

export type ReleaseStepId = `${string}#${ReleaseStepType}`;

export const generateBackwardsCompatibleReleaseStepRunId = (
    releaseId: string,
    stepArchetype: ReleaseStepType,
): ReleaseStepId => {
    return `${releaseId}#${stepArchetype}`;
};

export interface TranslationStringsByKey {
    [key: string]: TranslationString;
}

export interface TranslationStrings {
    byKey: TranslationStringsByKey;
    workingBranchStrings: TranslationStringsByKey;
    sourceLocale: string;
    latestPullRequest?: PullRequest;
    providerState?: TranslationProviderState;
}

export interface FeatureWorkItems {
    byId: FeatureWorkItemsById; // release-branch-based items
    workingBranchItemsById: FeatureWorkItemsById; // working branch based items
}

interface FeatureWorkItemsById {
    [id: string]: FeatureItem;
}

interface ReleaseStepsById {
    [id: ReleaseStepId]: ReleaseStep;
}

type ReleaseStepActionTypes =
    | FetchReleaseStepSuccessAction
    | TryUpdateReleaseStepsAction
    | ReleasesUpdatedAction
    | UpdateAppSettingsSuccessAction
    | UpdateReleaseSuccessAction;

function releaseStepsById(state: ReleaseStepsById = {}, action: ReleaseStepActionTypes) {
    switch (action.type) {
        case FETCH_RELEASE_STEP_SUCCESS: {
            const id = action.payload.releaseStep.id;
            return {
                [id]: action.payload.releaseStep,
                ...state,
            };
        }
        case UPDATE_RELEASE_STEPS: {
            const stepsState = {};
            for (const step of action.payload) {
                stepsState[step.id] = step;
            }
            return {
                ...state,
                ...stepsState,
            };
        }
        case UPDATE_RELEASE_SUCCESS: {
            const release = action.payload.release as Release & {
                steps: ReleaseStep[];
            };
            const stepsState = {};
            if (release.steps) {
                for (const step of release.steps) {
                    // skip updating steps that we've never loaded before
                    if (!state[step.id]) {
                        continue;
                    }

                    if (step.type === ReleaseStepType.Translations || step.type === ReleaseStepType.FeatureReadiness) {
                        // these steps DO have websockets that return the individual release step, they will be updated when those are triggered. Every other step right now is updated here with the release.steps data.
                        continue;
                    }
                    stepsState[step.id] = step;
                }
            }
            return {
                ...state,
                ...stepsState,
            };
        }
        case RELEASES_UPDATED: {
            const connectedReleases = action.payload;

            const stepsState = {};
            for (const connectedRelease of connectedReleases) {
                const release = connectedRelease.release as Release & {
                    steps: ReleaseStep[];
                };
                if (!('steps' in release)) {
                    continue;
                }

                for (const step of release.steps) {
                    // skip updating steps that we've never loaded before
                    if (!state[step.id]) {
                        continue;
                    }

                    if (step.type === ReleaseStepType.FeatureReadiness || step.type === ReleaseStepType.Translations) {
                        // don't overwrite step data for translations or FR steps, because it can sometimes get modified as part of the ReleaseStepEntity ws model
                        const stepOriginalData = state[step.id]?.data;

                        stepsState[step.id] = {
                            ...step,
                            data: stepOriginalData,
                        };
                    } else {
                        stepsState[step.id] = step;
                    }
                }
            }

            return {
                ...state,
                ...stepsState,
            };
        }
        default:
            return state;
    }
}

const releaseStepsReducer = combineReducers({
    byId: releaseStepsById,
});

export default releaseStepsReducer;
